// src/services/environdecService.js
const PROXY_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api/epd-international"
    : process.env.REACT_APP_ENVIRONDEC_PROXY_TARGET || "https://www.embodix.in/api/epd-international";

function toCacheKey(method, path, body) {
  if (method === "GET") return `GET:${path}`;
  return `${method}:${path}:${body ? JSON.stringify(body) : ""}`;
}

const RESOURCE_CACHE = new Map();

async function fetchJSON(path, { method = "GET", body, signal } = {}) {
  const cacheKey = toCacheKey(method, path, body);
  if (!signal && method === "GET" && RESOURCE_CACHE.has(cacheKey)) return RESOURCE_CACHE.get(cacheKey);

  const url = `${PROXY_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  const maxRetries = 2;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) {
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        ...(method === "POST" ? { "Content-Type": "application/json" } : {})
      },
      body: method === "POST" ? JSON.stringify(body || {}) : undefined,
      signal
    });

    if (response.ok) {
      const data = await response.json();
      if (!signal && method === "GET") RESOURCE_CACHE.set(cacheKey, data);
      return data;
    }

    const retryAfterHeader = response.headers.get("Retry-After");
    if ((response.status === 429 || response.status === 503) && attempt < maxRetries) {
      const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
      const waitMs = Number.isFinite(retryAfterSeconds)
        ? Math.max(500, retryAfterSeconds * 1000)
        : 800 * (attempt + 1);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      continue;
    }

    let details = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text();
    }

    const detailsText = typeof details === "string" ? details : JSON.stringify(details);
    const err = new Error(`EPD International request failed (${response.status}) ${detailsText}`.trim());
    err.status = response.status;
    err.retryAfter = retryAfterHeader;
    throw err;
  }
}

export async function fetchEnvirondecFilters({ signal } = {}) {
  return fetchJSON("?op=filters", { signal });
}

export async function searchEnvirondecEpds({
  searchString,
  country,
  category,
  sector,
  pageNumber = 1,
  pageSize = 50,
  signal
} = {}) {
  const skip = Math.max(0, (Number(pageNumber) - 1) * Number(pageSize));
  const params = new URLSearchParams();
  if (searchString) params.set("search", searchString);
  if (country) params.set("country", Array.isArray(country) ? country.join(",") : country);
  if (category) params.set("category", Array.isArray(category) ? category.join(",") : category);
  if (sector) params.set("sector", sector);
  params.set("skip", String(skip));
  params.set("take", String(pageSize));
  return fetchJSON(`?op=search&${params.toString()}`, { signal });
}

export async function fetchEnvirondecDetails({ registrationNumber, signal } = {}) {
  return fetchJSON(`?op=detail&registrationNumber=${encodeURIComponent(registrationNumber)}`, { signal });
}

export async function parseEnvirondecIlcd({ registrationNumber, signal } = {}) {
  return fetchJSON(`?op=ilcd&registrationNumber=${encodeURIComponent(registrationNumber)}`, {
    method: "POST",
    body: {},
    signal
  });
}
